// web/src/console/sections/ActivitySection.tsx
import type { JSX } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { consoleApi } from "../../lib/consoleApi";
import { usePaged } from "../usePaged";
import { Pager } from "../Pager";
import { PagedTable } from "../PagedTable";
import { UserLink } from "../UserLink";
import { SessionStateBadge } from "../SessionStateBadge";
import { fmtRelative } from "../format";
import { annotationMeta } from "../annotationMeta";
import { eventMeta } from "../eventMeta";
import { IconThumbUp, IconThumbDown, IconMessage, IconHeadset, IconChevron } from "../../components/icons";

type Tab = "conversations" | "annotations" | "events";
const TABS: { id: Tab; label: string }[] = [
  { id: "conversations", label: "会话" }, { id: "annotations", label: "用户反馈" }, { id: "events", label: "系统事件" }
];

// The 用户反馈 tab shows human feedback only (赞/踩/转人工). System bookkeeping
// kinds (cache_*, block_state) live under 系统事件 / telemetry, not here.
const HUMAN_FEEDBACK_KINDS = ["rating", "handoff"];

function annoIcon(key: "up" | "down" | "handoff" | "text"): JSX.Element {
  if (key === "up") return <IconThumbUp size={15} />;
  if (key === "down") return <IconThumbDown size={15} />;
  if (key === "handoff") return <IconHeadset size={15} />;
  return <IconMessage size={15} />;
}

function ConversationsTab(): JSX.Element {
  const navigate = useNavigate();
  const paged = usePaged((off) => consoleApi.fetchConversations({ offset: off }).then((r) => r.conversations), []);
  if (paged.loading) return <div className="cc-skeleton" style={{ height: 200 }} />;
  if (paged.error) return <div className="cc-empty">加载失败。 <button className="cc-back" onClick={paged.reload}>重试</button></div>;
  if (paged.rows.length === 0) return <div className="cc-empty">暂无会话</div>;
  return (
    <>
      <div className="cc-rows">
        {paged.rows.map((c) => (
          <div key={c.sessionId} className="cc-row" onClick={() => navigate(`/admin/conversations/${encodeURIComponent(c.sessionId)}`)} role="button" tabIndex={0}>
            <span className="cc-row-icon cc-tone-info"><IconMessage size={16} /></span>
            <div className="cc-row-main">
              <div className="cc-row-title">{c.title || "(无标题)"}</div>
              <div className="cc-row-meta">
                <SessionStateBadge state={c.state} />
                <span onClick={(e) => e.stopPropagation()}><UserLink userId={c.userId} username={c.username} /></span>
                <span>· {c.turnCount} 轮</span>
                {c.handedOff && <span className="cc-tag cc-tone-warn">转人工</span>}
                <span>· {fmtRelative(c.updatedAt)}</span>
              </div>
            </div>
            <IconChevron size={16} />
          </div>
        ))}
      </div>
      <Pager page={paged.page} hasMore={paged.hasMore} onPrev={paged.prev} onNext={paged.next} loading={paged.loading} />
    </>
  );
}

function AnnotationsTab(): JSX.Element {
  const navigate = useNavigate();
  const paged = usePaged((off) => consoleApi.fetchAnnotations({ offset: off, kinds: HUMAN_FEEDBACK_KINDS }).then((r) => r.annotations), []);
  if (paged.loading) return <div className="cc-skeleton" style={{ height: 200 }} />;
  if (paged.error) return <div className="cc-empty">加载失败。 <button className="cc-back" onClick={paged.reload}>重试</button></div>;
  if (paged.rows.length === 0) return <div className="cc-empty">暂无用户反馈</div>;
  return (
    <>
      <p className="cc-tab-note">用户对回答的反馈：赞 / 踩、转人工。点任意一条可跳到对话中的对应位置。</p>
      <div className="cc-rows">
        {paged.rows.map((a) => {
          const m = annotationMeta(a);
          const href = `/admin/conversations/${encodeURIComponent(a.sessionId)}${m.focusKey ? `?focus=${encodeURIComponent(m.focusKey)}` : ""}`;
          return (
            <div key={a.id} className="cc-row" onClick={() => navigate(href)} role="button" tabIndex={0} title="跳转到对话中的对应位置">
              <span className={`cc-row-icon cc-tone-${m.tone}`}>{annoIcon(m.iconKey)}</span>
              <div className="cc-row-main">
                <div className="cc-row-title"><span className={`cc-tag cc-tone-${m.tone}`} title={m.tip}>{m.label}</span> {m.snippet}</div>
                <div className="cc-row-meta">
                  <span onClick={(e) => e.stopPropagation()}><UserLink userId={a.userId} username={a.username} /></span>
                  <span>· {a.status}</span>
                  <span>· {fmtRelative(a.createdAt)}</span>
                </div>
              </div>
              <IconChevron size={16} />
            </div>
          );
        })}
      </div>
      <Pager page={paged.page} hasMore={paged.hasMore} onPrev={paged.prev} onNext={paged.next} loading={paged.loading} />
    </>
  );
}

function EventsTab(): JSX.Element {
  const paged = usePaged((off) => consoleApi.fetchEvents({ offset: off }).then((r) => r.events), []);
  return (
    <>
      <p className="cc-tab-note">系统运行与请求遥测：接口请求、在线心跳、对话生命周期、登录等。多为系统产生，非用户操作。</p>
      <PagedTable paged={paged} headers={["类型", "操作者", "路径", "状态", "时间"]} renderRow={(e) => {
        const em = eventMeta(e.eventType);
        return (
          <tr key={e.id}>
            <td><span title={em.tip}>{em.label}</span></td>
            <td>{e.actor ?? "—"}</td><td>{e.method} {e.path}</td><td>{e.status ?? ""}</td><td>{fmtRelative(e.createdAt)}</td>
          </tr>
        );
      }} />
    </>
  );
}

export function ActivitySection(): JSX.Element {
  const [tab, setTab] = useState<Tab>("conversations");
  return (
    <>
      <div className="cc-tabs">
        {TABS.map((t) => <button key={t.id} className={`cc-range${tab === t.id ? " active" : ""}`} onClick={() => setTab(t.id)}>{t.label}</button>)}
      </div>
      <div className="cc-card">
        {tab === "conversations" && <ConversationsTab />}
        {tab === "annotations" && <AnnotationsTab />}
        {tab === "events" && <EventsTab />}
      </div>
    </>
  );
}
